import { EventObject } from '@xstate/store-react';
import { StoreContext } from '../../../models/store/context';
import { log } from '@domgen/shared/utils/log';
import { StoreEvent } from '../../../enums/storeEvent';
import { getCatalogue } from '../../../services/api/getCatalogue';
import { store } from '../../store';

/**
 * @description Get Brand Catalogue
 * This event is responsible for fetching the brand catalogue when the app initializes. It does this by:
 * 1. Logging the catalogue fetch attempt
 * 2. Making an API call to retrieve the brand catalogue
 * 3. If successful, it sends a SET_CATALOGUE event with the retrieved catalogue to update the store
 * 4. If it fails to retrieve the catalogue, it logs a warning and sends a GET_CATALOGUE_FAILED event
 * @param {StoreContext} context - The current store context (not used in this event)
 * @param {EventObject | undefined} _event - The event object (not used in this event)
 * @param {{ effect: (effect: () => Promise<void>) => void }} enqueue - An object with an effect function to enqueue side effects
 * @returns {void} This event does not return a value
 * @sideEffect This event performs a side effect by making an API call to fetch the brand catalogue and updating the store based on the result
 */
export const getBrandCatalogue = <T extends EventObject | undefined = EventObject>(
  _context: StoreContext,
  _event: T,
  enqueue: { effect: (effect: () => Promise<void>) => void },
): void => {
  log.debug({ message: StoreEvent.GET_CATALOGUE });
  enqueue.effect(async () => {
    try {
      const brandCatalogue = await getCatalogue();
      store.send({ type: StoreEvent.SET_CATALOGUE, data: brandCatalogue });
    } catch (error) {
      console.warn('Failed to fetch catalogue', error);
      store.send({ type: StoreEvent.GET_CATALOGUE_FAILED });
    }
  });
};
